import {Api} from 'laravel-request';

import {makeRequest} from '../chats/makeRequest';
import {TaskContactDTO} from '../dto/contactDTO';
import {clearChatId} from '../middleware/websocketConnector';
import type { ApiResponse , Client } from '../types/api.types';
import {
    CallbackOptions,
    ChatLastMessagesByPhone,
    ClientRatingUpdateParams,
    PossiblyBooleanApiParam,
    SortIdCases,
} from '../types/contracts';
import type { LaravelQueryBuilder } from '../types/laravelRequest';
import type { ApiSuccessHandler } from '../types/legacyCallbacks';


export const CLIENT_RELATIONS = [
    'atonUsers',
    'manager',
] as const;

export const MULTIPLE_MANAGERS = [
    'multiple_managers',
    'multiple_managers_pivot.manager'
] as const;

export const FINANCE_GROUP_RELATIONS = [
    'financeGroups',
    'financeGroups.unionUser',
    'oppositeFinanceGroup',
    'collectiveGroups',
    'collectiveGroups.user',
    'collectiveGroups.unionUser',
] as const;

export const MAIN_CONTACT_RELATIONS_LITE = (contactQuery: LaravelQueryBuilder) =>
    contactQuery.with('refuseReason');

export const MAIN_CONTACT_RELATIONS = (contactQuery: LaravelQueryBuilder) =>
    MAIN_CONTACT_RELATIONS_LITE(contactQuery).with(
        'tasks',
        (tasksQuery: LaravelQueryBuilder) => tasksQuery.orderBy('end_at', 'DESC')
    );

export type BuildFetchItemsQueryOptions = {
    /** Только для сортировки по дате контакта (sortId 7/8) */
    withContactTasks?: boolean;
};

export const buildFetchItemsQuery = (
    clientId: string | number | null,
    isArchive: PossiblyBooleanApiParam,
    sortId: SortIdCases | null,
    isFullGroup: PossiblyBooleanApiParam = false,
    options: BuildFetchItemsQueryOptions = {}
): LaravelQueryBuilder => {
    const mainContactRelations = options.withContactTasks
        ? MAIN_CONTACT_RELATIONS
        : MAIN_CONTACT_RELATIONS_LITE;
    const query = Api.get('client', 'index')
        .with(...CLIENT_RELATIONS)
        .with('multiple_managers_pivot.manager')
        .with('mainContact', mainContactRelations).with('multiple_managers') as any as LaravelQueryBuilder;
    if (isFullGroup) query.with(...FINANCE_GROUP_RELATIONS);

    query.whereHas('mainContact', (contactQuery: LaravelQueryBuilder) => contactQuery.where(
            'is_archive',
            Number(Boolean(isArchive))
        )
    );

    if (clientId) query.where('id', clientId);

    return query;
};

export const getClientTransitions = (
    callbackOnSuccess: ApiSuccessHandler<any>
) => {
    return Api.get('crm-client-transition', 'index').all((response) => {
        callbackOnSuccess && callbackOnSuccess(response);
    });
};

export const callbackOnFetchItemsSuccess = (
    {
        response,
        userData,
        actions
    }: CallbackOptions
): void => {
    const {appendLastMessages, sortItems, setLoading} = actions;
    const clients = response.data || [];
    const phones = clients
        .filter((client) => client.mainContact?.phone)
        .map((client) => clearChatId(client.mainContact?.phone ?? ''));

    makeRequest({
        url: 'chat/last',
        data: {phones},
        userId: userData?.id,
        callback: (lastMessages: any) => {
            const updatedClients = appendLastMessages(lastMessages as ChatLastMessagesByPhone, [...clients]);
            sortItems(updatedClients);
            setLoading(false);
        },
        errorCallback: () => {
            sortItems([...clients]);
            setLoading(false);
        },
    });
};

export const getTaskClients = (
    userId: string | number,
    onResult: (contacts: TaskContactDTO[]) => void
) => {
    Api.get('client', 'index')
        .where('manager_id', userId)
        .all((clientResponse: ApiResponse<unknown[]>) => {
            Api.get('crm-contact', 'index')
                .where('user_id', userId)
                .all((contactResponse: ApiResponse<unknown[]>) => {
                    const taskContacts = [
                        ...TaskContactDTO.fromArray(clientResponse.data as Parameters<typeof TaskContactDTO.fromArray>[0], 'user'),
                        ...TaskContactDTO.fromArray(contactResponse.data as Parameters<typeof TaskContactDTO.fromArray>[0], 'contact'),
                    ];
                    onResult(taskContacts);
                });
        });
};

export const getClientsWithRelations = (
    callbackOnSuccess: ApiSuccessHandler<Client[]>,
    callbackOnError?: (error: any) => void
) => {
    Api.get('client', 'index')
        .with(...CLIENT_RELATIONS)
        .with('mainContact', MAIN_CONTACT_RELATIONS)
        .with(...FINANCE_GROUP_RELATIONS)
        .with(...MULTIPLE_MANAGERS)
        .all(
            (response: ApiResponse<Client[]>) => callbackOnSuccess && callbackOnSuccess(response),
            (error) => callbackOnError && callbackOnError(error),
        );
};

export const getUserFinanceGroup = (
    userId: number | string,
    responseCallback: (data: any) => void
) => {
    Api.get('user-finance-group', 'index')
        .with('unionUser')
        .where('user_id', userId)
        .all(({data}: ApiResponse<any>) => {
            responseCallback(data);
        });
}

export const putUpdateClientField = (
    clientId: number | string,
    params: ClientRatingUpdateParams,
    responseCallback: (response: ApiResponse<any>) => void,
    errorCallback?: (error: any) => void
) => {
    Api.putArg(
        'client',
        'update-field',
        clientId,
        params
    ).withoutNotify().call(
        (response: ApiResponse<any>) => response && responseCallback && responseCallback(response),
        (error) => error && errorCallback && errorCallback(error)
    );
}
